import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  filter,
  merge,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  withLatestFrom,
} from 'rxjs';

import { LetDirective } from '@ngrx/component';
import { TranslateService } from '@ngx-translate/core';

import { Account } from '@personal-area/global/accounts/models';
import { AccountSocketService } from '@personal-area/global/accounts/services';
import { TransactionSocketService } from '@personal-area/global/billing/feature';
import { UnsubscribeController } from '@personal-area/global/common'

import { LeverageComponentStore } from './account-viewer-leverage.component.store';

export const ACCOUNT_UPDATE_THROTTLE_TIME = 1000;

@Component({
    selector: 'global-account-viewer-leverage',
    templateUrl: './account-viewer-leverage.component.html',
    styleUrls: ['./account-viewer-leverage.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LetDirective],
    providers: [LeverageComponentStore]
})
export class AccountViewerLeverageComponent
  extends UnsubscribeController
  implements OnChanges, OnInit
{
  @Input() account!: Account;

  public settings$ = this._componentStore.settings$;
  public isChangeDisabled$ = this._componentStore.isChangeDisabled$;

  constructor(
    private _accountSocket: AccountSocketService,
    private _translateService: TranslateService,
    private _transactionSocketService: TransactionSocketService,
    private _componentStore: LeverageComponentStore
  ) {
    super();
  }

  public ngOnInit() {
    // Re-translate settings on lang change
    // Re-calculate available leverages on transaction succeeding
    merge(
      this._translateService.onLangChange,
      this._transactionSocketService.transactionUpdated
    )
      .pipe(
        throttleTime(ACCOUNT_UPDATE_THROTTLE_TIME),
        takeUntil(this._unsubscribe)
      )
      .subscribe(() => this._componentStore.getLeverage(this.account));

    // Wait for proof that the leverage has been changed
    this._accountSocket.accountLeverage
      .pipe(
        filter(({ AccountId }) => AccountId === this.account.id),
        withLatestFrom(this.settings$),
        tap(([{ leverage: current }, settings]) =>
          this._componentStore.setState({
            isChangeDisabled: false,
            settings: {...settings, current},
          })
        ),
        switchMap(() => this._accountSocket.accountUpdated),
        takeUntil(this._unsubscribe)
      )
      .subscribe(
        // Update leverage settings in case of equity change
        () => this._componentStore.getLeverage(this.account)
      );
  }

  public ngOnChanges(changes: SimpleChanges) {
    this._componentStore.getLeverage(this.account);
  }

  public changeLeverage(): void {
    this._componentStore.changeLeverage(this.account);
  }
}
